import { useState, useEffect } from 'react';
import './StarterScreen.css';
import GameSetupForm from './GameSetupForm';
import Button from '@mui/material/Button';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { isMobileDevice } from '../helper/DeviceDetection';

interface StarterScreenProps {
  onDemoGame: () => void;
  onSetupGame: () => void;
  onHighScores: () => void;
}

export default function StarterScreen(props: StarterScreenProps) {
  const [showSetupForm, setShowSetupForm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Check if device is mobile on mount
    setIsMobile(isMobileDevice());
    
    // Also check on window resize
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <div className="starter-screen">
      <div className="starter-content">
        <h1 className="starter-title">🏃‍♂️📊 Idle Sprint Planning Simulator</h1>
        
        {isMobile && (
          <div className="mobile-warning">
            <p>
              <strong>⚠️ Mobile Device Detected</strong>
            </p>
            <p>
              This application is designed for PC and monitor use (preferably 1080p or higher). 
              The game requires a larger screen for the best experience and full functionality.
            </p>
          </div>
        )}
        
        {!isMobile && (
          <div className="desktop-notice">
            <p>
              <strong>💻 Desktop Experience</strong>
            </p>
            <p>
              This application works best on a PC with a monitor at 1080p resolution or higher.
            </p>
          </div>
        )}
        
        <div className="starter-description">
          <h2>Welcome to the Ultimate Agile Learning Experience!</h2>
          
          <p>
            Step into the role of a product manager and discover what it takes to build successful software 
            through the exciting world of Agile methodologies. This interactive simulation game teaches you 
            the fundamentals of Scrum and sprint planning in a fun, hands-on way.
          </p>
          
          <h3>What You'll Learn:</h3>
          <ul>
            <li><strong>Sprint Planning:</strong> Master the art of scoping work for time-boxed iterations</li>
            <li><strong>Team Management:</strong> Understand how different specializations affect productivity</li>
            <li><strong>Backlog Prioritization:</strong> Balance customer demands with technical needs</li>
            <li><strong>Velocity Tracking:</strong> Use metrics to predict capacity and improve performance</li>
            <li><strong>Release Management:</strong> Deploy features strategically and gather feedback</li>
            <li><strong>Customer Focus:</strong> Deliver value while managing competing stakeholder priorities</li>
          </ul>
          
          <h3>How It Works:</h3>
          <p>
            You'll manage a team of four specialized developers building a restaurant management system 
            for various customers. Each 10-day sprint cycle challenges you to:
          </p>
          <ul>
            <li>Select issues from your backlog based on customer requirements</li>
            <li>Assign work to developers with different skills (UI/UX, Backend, Database, DevOps)</li>
            <li>Watch your team progress through coding and QA phases</li>
            <li>Release completed features to unlock new customers and revenue</li>
            <li>Track your team's velocity and continuously improve your planning</li>
          </ul>
          
          <h3>Game Features:</h3>
          <ul>
            <li>📦 <strong>120+ Issues</strong> across 11 feature areas (epics)</li>
            <li>🎯 <strong>8+ Unique Customers</strong> with specific requirements and punny names</li>
            <li>⚒️ <strong>12 Team Members</strong> to hire and manage with different specializations</li>
            <li>📊 <strong>Real-time Metrics</strong> including velocity charts and epic progress</li>
            <li>💰 <strong>Financial Management</strong> with recurring revenue and team wages</li>
          </ul>
          
          <p className="starter-note">
            Whether you're new to Agile or looking to sharpen your sprint planning skills, this simulator 
            provides a risk-free environment to experiment, learn from mistakes, and master the principles 
            that drive successful software teams.
          </p>
        </div>
        
        <div className="starter-buttons">
          <button 
            className={`starter-button setup-button ${isMobile ? 'disabled' : ''}`}
            onClick={() => setShowSetupForm(true)}
            disabled={isMobile}
            title={isMobile ? 'Not available on mobile devices' : ''}
          >
            ⚙️ Set Up Game
          </button>
          <button 
            className={`starter-button demo-button ${isMobile ? 'disabled' : ''}`}
            onClick={props.onDemoGame}
            disabled={isMobile}
            title={isMobile ? 'Not available on mobile devices' : ''}
          >
            🎮 Demo Game
          </button>
          <button className="starter-button highscores-button" onClick={props.onHighScores}>
            🏆 High Scores
          </button>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FeedbackIcon />}
            onClick={() => window.open('https://feedback.waynerockett.com/idle-sprint-planning-simulator', '_blank')}
            sx={{
              textTransform: 'none',
              boxShadow: 2,
              fontSize: '1rem',
              padding: '10px 24px',
              '&:hover': {
                boxShadow: 4,
              }
            }}
            data-testid="feedback-button"
          >
            Send Feedback
          </Button>
        </div>
      </div>
      {showSetupForm && (
        <GameSetupForm onClose={() => setShowSetupForm(false)} />
      )}
    </div>
  );
}
